import { attach, createEffect, createStore, restore } from 'effector';

import { request } from 'src/common/request';
import { Group, Member } from 'src/domain/group';
import { FetchCollectionResponse } from 'src/domain/response-collection';
import { user$ } from 'src/store/user.store';

const MEMBERS_TAKE = 15;

type CreateGroupPayload = {
	caption: string;
	description: string;
	private: boolean;
	key: string | null;
	creator?: string;
	event_date: string;
	distribution_date: string;
}
type UpdateGroupPayload = {
	group?: string;
	caption: string;
	description: string;
}

export const group$ = createStore<Group | null>(null);

export const fetchGroupFx = attach({
	source: { user: user$ },
	mapParams: (payload: { group: string }, source) => ({
		group: payload.group,
		user: source.user?.id,
	}),
	effect: createEffect<{ group: string, user?: string }, Group>({
		handler: async (payload) => {
			const { data } = await request.get(`/groups/${payload.group}`, {
				params: { user: payload.user },
			});

			return data;
		},
	}),
});
export const fetchMembersFx = attach({
	source: { group: group$ },
	mapParams: (payload: { page: number }, source) => ({
		page: payload.page,
		group: source.group?.id,
	}),
	effect: createEffect({
		handler: async (payload: { group?: string, page: number }): Promise<FetchCollectionResponse<Member>> => {
			const { data: { data, meta } } = await request.get(`/groups/${payload.group}/members`, {
				params: {
					take: MEMBERS_TAKE,
					skip: payload.page * MEMBERS_TAKE,
				},
			});

			return { data, meta: { page: payload.page, total: Math.ceil(meta.total / MEMBERS_TAKE) } };
		},
	}),
});
export const groupMemberFx = attach({
	source: { group: group$ },
	mapParams: (payload: { user?: string, key?: string, type: 'add' | 'remove' }, source) => ({
		user: payload.user,
		group: source.group?.id,
		type: payload.type,
		key: payload.key,
	}),
	effect: createEffect<{ type: 'add' | 'remove'; key?: string, group?: string; user?: string }, Member>({
		handler: async (payload) => {
			const url = `/groups/${payload.group}/members`;
			const body = { user: payload.user, key: payload.key };
			let member: Member;

			if (payload.type === 'add') {
				const { data } = await request.post(url, body);
				member = data;
			} else {
				const { data } = await request.delete(url, { data: body });
				member = data;
			}

			return member;
		},
	}),
});
export const createGroupFx = attach({
	source: { user: user$ },
	mapParams: (payload: Omit<CreateGroupPayload, 'creator'>, source) => ({
		...payload,
		creator: source.user?.id,
	}),
	effect: createEffect<CreateGroupPayload, Group>({
		handler: async (payload) => {
			const { data } = await request.post('/groups', payload);

			return data;
		},
	}),
});
export const updateGroupFx = attach({
	source: { group: group$ },
	mapParams: (payload: {caption: string; description: string}, source) => ({
		...payload,
		group: source.group?.id
	}),
	effect: createEffect<UpdateGroupPayload, Group>({
		handler: async ({ group, ...payload }) => {
			const { data } = await request.patch(`/groups/${group}`, payload);

			return data;
		},
	}),
});
export const distributeGroupFx = attach({
	source: { group: group$ },
	mapParams: (_: void, source) => ({
		id: source.group?.id,
	}),
	effect: createEffect<{ id?: string }, Group>({
		handler: async (payload) => {
			const { data } = await request.get(`/groups/${payload.id}/distribute`);

			return data;
		},
	}),
});
export const deleteGroupFx = attach({
	source: { group: group$ },
	mapParams: (_: void, source) => ({ id: source.group?.id }),
	effect: createEffect({
		handler: async (payload: { id?: string }) => {
			await request.delete(`/groups/${payload.id}`);
		},
	}),
});

export const members$ = restore(fetchMembersFx.doneData, null);

group$
	.on(distributeGroupFx.doneData, (_, group) => group)
	.on(fetchGroupFx.doneData, (_, result) => result)
	.on(groupMemberFx.done, (state, payload) => {
		if (!state) { return state; }

		if (payload.params.type === 'add') {
			return {
				...state,
				members: state.members + 1,
				joined_user: payload.result,
			};
		} else {
			return {
				...state,
				members: state.members - 1,
				joined_user: null,
			};
		}
	});
